import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DollarSign, Plus, Edit2, Trash2, Save, X, Users, Package, TrendingUp, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import BackButton from '../components/BackButton';
import { getToken } from '../services/tokenService';
import ActivityBillingDialogs from '../components/ActivityBillingDialogs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminActivityBilling = () => {
  const _navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState('oscop_ge');
  const [activities, setActivities] = useState([]);
  const [activityPlans, setActivityPlans] = useState([]);
  const [activityCredits, setActivityCredits] = useState([]);
  const [stats, setStats] = useState({});
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [_editingCredit, setEditingCredit] = useState(null);

  // Form states
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price: '',
    plan_type: 'subscription',
    billing_period: 'monthly',
    features: [],
    is_popular: false
  });

  const [creditForm, setCreditForm] = useState({
    name: '',
    description: '',
    credits: '',
    price: '',
    credit_value: '',
    validity_days: ''
  });

  const planTypes = {
    oscop_ge: 'mise_a_disposition',
    formascop: 'formation',
    logistique_mobilite: 'transport',
    agro_vegetal: 'service',
    batiment_expertise: 'expertise',
    dispositifs_btp: 'subscription'
  };

  useEffect(() => {
    loadActivities();
  }, []);

  useEffect(() => {
    if (selectedActivity) {
      loadActivityData();
    }
  }, [selectedActivity]);

  const loadActivities = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/activity-billing/activities`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActivities(response.data.activities || []);
    } catch {
    }
  };

  const loadActivityData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      
      const [plansRes, creditsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/activity-billing/plans/${selectedActivity}`, 
          { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/activity-billing/credits/${selectedActivity}`, 
          { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/activity-billing/stats/${selectedActivity}`, 
          { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setActivityPlans(plansRes.data.plans || []);
      setActivityCredits(creditsRes.data.packages || []);
      setStats(statsRes.data || {});
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async () => {
    try {
      const token = getToken();
      await axios.post(
        `${BACKEND_URL}/api/admin/activity-billing/plans`,
        {
          activity_type: selectedActivity,
          plan_type: planTypes[selectedActivity],
          ...planForm,
          price: parseFloat(planForm.price),
          features: planForm.features.filter(f => f.trim() !== '')
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Plan créé avec succès !');
      setShowPlanDialog(false);
      resetPlanForm();
      loadActivityData();
    } catch {
      alert('❌ Erreur lors de la création');
    }
  };

  const handleUpdatePlan = async () => {
    try {
      const token = getToken();
      await axios.put(
        `${BACKEND_URL}/api/admin/activity-billing/plans/${editingPlan.plan_id}`,
        {
          ...planForm,
          price: parseFloat(planForm.price),
          features: planForm.features.filter(f => f.trim() !== '')
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Plan mis à jour avec succès !');
      setShowPlanDialog(false);
      setEditingPlan(null);
      resetPlanForm();
      loadActivityData();
    } catch {
      alert('❌ Erreur lors de la mise à jour');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Supprimer ce plan ?')) return;

    try {
      const token = getToken();
      await axios.delete(
        `${BACKEND_URL}/api/admin/activity-billing/plans/${planId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Plan supprimé !');
      loadActivityData();
    } catch {
      alert('❌ Erreur lors de la suppression');
    }
  };

  const handleCreateCredit = async () => {
    try {
      const token = getToken();
      await axios.post(
        `${BACKEND_URL}/api/admin/activity-billing/credits`,
        {
          activity_type: selectedActivity,
          ...creditForm,
          credits: parseInt(creditForm.credits),
          price: parseFloat(creditForm.price),
          credit_value: parseFloat(creditForm.credit_value),
          validity_days: creditForm.validity_days ? parseInt(creditForm.validity_days) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Package créé avec succès !');
      setShowCreditDialog(false);
      resetCreditForm();
      loadActivityData();
    } catch {
      alert('❌ Erreur lors de la création');
    }
  };

  const handleDeleteCredit = async (packageId) => {
    if (!window.confirm('Supprimer ce package ?')) return;

    try {
      const token = getToken();
      await axios.delete(
        `${BACKEND_URL}/api/admin/activity-billing/credits/${packageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Package supprimé !');
      loadActivityData();
    } catch {
      alert('❌ Erreur lors de la suppression');
    }
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: '',
      description: '',
      price: '',
      plan_type: 'subscription',
      billing_period: 'monthly',
      features: [],
      is_popular: false
    });
  };

  const resetCreditForm = () => {
    setCreditForm({
      name: '',
      description: '',
      credits: '',
      price: '',
      credit_value: '',
      validity_days: ''
    });
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name,
      description: plan.description,
      price: plan.price.toString(),
      plan_type: plan.plan_type,
      billing_period: plan.billing_period,
      features: plan.features || [],
      is_popular: plan.is_popular
    });
    setShowPlanDialog(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Hero Header — Ultra Pro */}
        <div className="mb-6">
          <BackButton to="/admin/dashboard" label="Retour" />
        </div>
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-emerald-900 to-teal-900 p-6 lg:p-8 text-white shadow-2xl mb-6">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(16,185,129,0.4),_transparent_55%)] pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(20,184,166,0.25),_transparent_45%)] pointer-events-none" />
          <div className="relative">
            <div className="flex items-start gap-4 mb-2">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 via-green-500 to-teal-400 flex items-center justify-center shadow-lg shadow-emerald-500/40 flex-shrink-0">
                <DollarSign className="h-7 w-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/30 backdrop-blur-sm font-semibold text-xs flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Modèles éditables en direct
                  </span>
                </div>
                <h1 className="text-2xl lg:text-3xl font-bold">Modèles Économiques par Activité</h1>
                <p className="text-emerald-100 mt-1 text-sm lg:text-base">
                  Gérez les plans et tarifs pour chaque activité O&apos;SCOP — chaque changement est appliqué dynamiquement aux abonnés.
                </p>
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
              <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
                <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold uppercase tracking-wider">
                  <Users className="h-3.5 w-3.5" /> Abonnés total
                </div>
                <p className="text-3xl font-bold mt-1">{stats.total_subscribers || 0}</p>
                <p className="text-xs text-emerald-100 mt-0.5">tous statuts confondus</p>
              </div>
              <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
                <div className="flex items-center gap-2 text-green-300 text-xs font-semibold uppercase tracking-wider">
                  <CheckCircle className="h-3.5 w-3.5" /> Actifs
                </div>
                <p className="text-3xl font-bold mt-1">{stats.active_subscribers || 0}</p>
                <p className="text-xs text-emerald-100 mt-0.5">
                  {stats.total_subscribers ? Math.round((stats.active_subscribers || 0) * 100 / stats.total_subscribers) : 0}% de conversion
                </p>
              </div>
              <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
                <div className="flex items-center gap-2 text-cyan-300 text-xs font-semibold uppercase tracking-wider">
                  <Package className="h-3.5 w-3.5" /> Plans actifs
                </div>
                <p className="text-3xl font-bold mt-1">{activityPlans.length}</p>
                <p className="text-xs text-emerald-100 mt-0.5">
                  {activityPlans.filter(p => p.is_popular).length} marqué(s) populaire
                </p>
              </div>
              <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
                <div className="flex items-center gap-2 text-yellow-300 text-xs font-semibold uppercase tracking-wider">
                  <TrendingUp className="h-3.5 w-3.5" /> Packages crédits
                </div>
                <p className="text-3xl font-bold mt-1">{activityCredits.length}</p>
                <p className="text-xs text-emerald-100 mt-0.5">offres prépayées disponibles</p>
              </div>
            </div>

            {/* Selector activité visuel */}
            {activities.length > 0 && (
              <div className="mt-5 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-200 mb-3">Activité O&apos;SCOP en cours d&apos;édition</p>
                <div className="flex flex-wrap gap-2">
                  {activities.map(a => (
                    <button
                      key={a.id}
                      onClick={() => setSelectedActivity(a.id)}
                      className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm border transition-all ${
                        selectedActivity === a.id
                          ? 'bg-gradient-to-r from-emerald-400 to-teal-400 text-slate-900 border-transparent shadow-lg shadow-emerald-500/30 font-semibold'
                          : 'bg-white/10 hover:bg-white/20 border-white/20 text-white'
                      }`}
                      data-testid={`activity-billing-tab-${a.id}`}
                    >
                      <span>{a.icon}</span>
                      <span>{a.name.split(' - ')[0]}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Onglets par activité */}
        <Card>
          <CardHeader>
            <CardTitle>Gestion par Activité</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={selectedActivity} onValueChange={setSelectedActivity}>
              <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6">
                {activities.map(activity => (
                  <TabsTrigger key={activity.id} value={activity.id}>
                    <span className="mr-2">{activity.icon}</span>
                    <span className="hidden lg:inline">{activity.name.split(' - ')[0]}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {activities.map(activity => (
                <TabsContent key={activity.id} value={activity.id} className="space-y-6">
                  {/* Plans */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold">Plans / Services</h3>
                      <Button
                        onClick={() => {
                          resetPlanForm();
                          setEditingPlan(null);
                          setShowPlanDialog(true);
                        }}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Nouveau Plan
                      </Button>
                    </div>

                    {loading ? (
                      <p className="text-gray-500">Chargement...</p>
                    ) : activityPlans.length === 0 ? (
                      <p className="text-gray-500">Aucun plan pour cette activité</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {activityPlans.map(plan => (
                          <Card key={plan.plan_id} className={plan.is_popular ? 'border-2 border-blue-500' : ''}>
                            <CardHeader>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                                  {plan.is_popular && (
                                    <span className="text-xs bg-blue-700 text-white px-2 py-1 rounded mt-1 inline-block">
                                      Populaire
                                    </span>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openEditPlan(plan)}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeletePlan(plan.plan_id)}
                                  >
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                              <CardDescription className="mt-2">{plan.description}</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-blue-600 mb-2">
                                {plan.price}€
                                <span className="text-sm text-gray-500 font-normal">
                                  {plan.billing_period === 'monthly' ? '/mois' : 
                                   plan.billing_period === 'yearly' ? '/an' : ''}
                                </span>
                              </div>
                              {plan.features && plan.features.length > 0 && (
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {plan.features.slice(0, 3).map((feature, idx) => (
                                    <li key={idx}>• {feature}</li>
                                  ))}
                                </ul>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Packages Crédits */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold">Packages de Crédits</h3>
                      <Button
                        onClick={() => {
                          resetCreditForm();
                          setEditingCredit(null);
                          setShowCreditDialog(true);
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Nouveau Package
                      </Button>
                    </div>

                    {activityCredits.length === 0 ? (
                      <p className="text-gray-500">Aucun package pour cette activité</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {activityCredits.map(pkg => (
                          <Card key={pkg.package_id}>
                            <CardHeader>
                              <div className="flex items-start justify-between">
                                <CardTitle className="text-lg">{pkg.name}</CardTitle>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteCredit(pkg.package_id)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                              <CardDescription>{pkg.description}</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-green-600 mb-2">
                                {pkg.credits} crédits → {pkg.price}€
                              </div>
                              <p className="text-sm text-gray-500">
                                Prix/crédit: {(pkg.price / pkg.credits).toFixed(2)}€
                              </p>
                              <p className="text-sm text-gray-500">
                                Valeur nominale: {pkg.credit_value}€
                              </p>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

        {/* Dialog Création/Édition Plan */}
        <ActivityBillingDialogs
          showPlanDialog={showPlanDialog}
          setShowPlanDialog={setShowPlanDialog}
          showCreditDialog={showCreditDialog}
          setShowCreditDialog={setShowCreditDialog}
          editingPlan={editingPlan}
          planForm={planForm}
          setPlanForm={setPlanForm}
          creditForm={creditForm}
          setCreditForm={setCreditForm}
          handleCreatePlan={handleCreatePlan}
          handleUpdatePlan={handleUpdatePlan}
          handleCreateCredit={handleCreateCredit}
          setEditingPlan={setEditingPlan}
          resetPlanForm={resetPlanForm}
          resetCreditForm={resetCreditForm}
        />
      </div>
    </div>
  );
};

export default AdminActivityBilling;
